/**
 * Versions tab of the site detail page. The version history rows moved here
 * unchanged from the pre-tab AeroSiteDetail: same plain-language timeline,
 * same kebab actions (open / publish / restore / discard) and the same
 * stale-state guards. The per-version route and page are untouched.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import AeroCardMenu from '../../../components/Aero/AeroCardMenu';
import { AeroStatus } from '../../../components/Aero/AeroHomeParts';
import { describeChange, formatVersionDate, formatBytes } from '../sitesUtils';

/**
 * One row of the History card. The whole row opens the version page; the
 * kebab opens a small state-dependent menu via the shared AeroCardMenu
 * (portalled, ut-theme, click-outside/scroll-close handled there).
 */
function VersionRow({ version, siteId, isActive, isDraft, publishingId, onPublish, onDiscard }) {
  const navigate = useNavigate();

  const openVersion = () => navigate(`/sites/${siteId}/versions/${version.id}`);

  // State-dependent actions: a draft can be published or discarded, an older
  // version can be restored, and every row can be opened.
  const menuItems = [{ label: 'Open version', onClick: openVersion }];
  if (isDraft) {
    menuItems.push({
      label: publishingId === version.id ? 'Publishing…' : 'Publish',
      onClick: () => onPublish(version.id),
      disabled: !!publishingId,
    });
    menuItems.push({
      label: 'Discard draft',
      onClick: () => onDiscard(version.id),
      danger: true,
      disabled: !!publishingId,
    });
  } else if (!isActive) {
    menuItems.push({
      label: publishingId === version.id ? 'Publishing…' : 'Restore this version',
      onClick: () => onPublish(version.id),
      disabled: !!publishingId,
    });
  }

  const detailParts = [describeChange(version)];
  if (version.fileCount) {
    detailParts.push(`${version.fileCount} ${version.fileCount === 1 ? 'file' : 'files'}`);
  }
  const sizeLabel = formatBytes(version.totalBytes);
  if (sizeLabel) detailParts.push(sizeLabel);

  // Where the publish came from (source-stamped versions). Versions written
  // before the stamp existed have no source and render no line at all.
  const sourceLine = (() => {
    if (version.source === 'web') return 'via web upload';
    if (version.source === 'api') {
      return version.sourceLabel ? `via API (${version.sourceLabel})` : 'via API';
    }
    if (version.source === 'mcp') {
      return version.sourceLabel ? `via ${version.sourceLabel} (agent)` : 'via agent';
    }
    return null;
  })();

  return (
    <div
      className="aero-sites-tl-entry"
      onClick={openVersion}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openVersion();
        }
      }}
    >
      <span className={`aero-sites-tl-dot${isActive ? ' live' : ''}${isDraft ? ' draft' : ''}`} />
      <div className="aero-sites-tl-body">
        <div className="aero-sites-tl-row">
          <span className="aero-sites-tl-summary">{formatVersionDate(version.createdAt)}</span>
          {isActive && <AeroStatus kind="live">Live</AeroStatus>}
          {isDraft && <AeroStatus kind="draft" />}
        </div>
        <div className="aero-sites-tl-time">{detailParts.join(' · ')}</div>
        {sourceLine && <div className="aero-sites-tl-source">{sourceLine}</div>}
      </div>
      <div className="aero-sites-tl-actions">
        <AeroCardMenu items={menuItems} />
      </div>
    </div>
  );
}

function SiteVersionsTab({ site, versions, versionsLoading, publishingId, publishVersion, discardDraft }) {
  const activeIndex = versions.findIndex((v) => v.id === site.activeVersion);

  return (
    <div className="aero-card aero-sites-history">
      <div className="aero-card-head">
        <h3>Versions</h3>
        <span className="aero-sites-history-note">Roll back to any of them; the address stays the same.</span>
      </div>
      {versionsLoading ? (
        <div className="aero-card-pad">
          <div className="ut-skeleton" style={{ width: '40%', height: 14 }} />
        </div>
      ) : versions.length === 0 ? (
        <div className="aero-card-pad">
          <p className="aero-page-sub" style={{ margin: 0 }}>
            No versions yet. Your first upload will appear here.
          </p>
        </div>
      ) : (
        <div className="aero-sites-timeline">
          {versions.map((version, i) => {
            const versionIsActive = version.id === site.activeVersion;
            // A draft is a version that isn't active yet and is newer than
            // the active one. When the active version isn't in the loaded
            // list at all, show no Draft badges rather than labeling every
            // row a discardable draft.
            const isDraft = !versionIsActive && activeIndex !== -1 && i < activeIndex;
            return (
              <VersionRow
                key={version.id}
                version={version}
                siteId={site.id}
                isActive={versionIsActive}
                isDraft={isDraft}
                publishingId={publishingId}
                onPublish={publishVersion}
                onDiscard={discardDraft}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

export default SiteVersionsTab;
